import "./Tooltip.css";
import { Children, forwardRef, useCallback } from "react";
import { Div } from "../../html";
import { augmentElement, isNil, mergeProps, resolveChildren, useControllableState, useEventCallback, useId, useMergedRefs } from "../../shared";
import { Overlay, OverlayArrow, isTargetParent, useOverlayLightDismiss, useOverlayPosition, useOverlayTrigger } from "../../overlay";
import { TooltipTriggerContext } from "./TooltipTriggerContext";
import { useResponsiveValue, useThemeContext } from "../../styling";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "div";
export function parseTooltipTrigger(children) {
  const array = Children.toArray(resolveChildren(children));

  if (array.length !== 2) {
    throw new Error("A tooltip trigger must have exactly 2 children.");
  }

  return array;
}
export function InnerTooltipTrigger({
  allowFlip = true,
  allowPreventOverflow = true,
  as = DefaultElement,
  children,
  containerElement,
  defaultOpen,
  disabled,
  forwardedRef,
  onOpenChange,
  open,
  position: positionProp = "top",
  zIndex = 10000,
  ...rest
}) {
  const positionValue = useResponsiveValue(positionProp);
  const {
    themeAccessor
  } = useThemeContext();
  const [isOpen, setIsOpen] = useControllableState(open, defaultOpen, false);
  const updateIsOpen = useCallback((event, newValue) => {
    if (isOpen !== newValue) {
      setIsOpen(newValue);

      if (!isNil(onOpenChange)) {
        onOpenChange(event, newValue);
      }
    }
  }, [onOpenChange, isOpen, setIsOpen]);
  const {
    arrowRef,
    overlayRef: overlayPositionRef,
    triggerRef
  } = useOverlayPosition({
    allowFlip,
    allowPreventOverflow,
    boundaryElement: containerElement,
    hasArrow: true,
    position: positionValue
  });
  const overlayRef = useMergedRefs(overlayPositionRef, forwardedRef);
  const triggerProps = useOverlayTrigger(isOpen, {
    hideOnLeave: true,
    isDisabled: disabled,
    onHide: useEventCallback(event => {
      // Prevent from closing when the focus goes to an element of the overlay on opening.
      if (!isTargetParent(event.relatedTarget, overlayRef)) {
        updateIsOpen(event, false);
      }
    }),
    onShow: useEventCallback(event => {
      updateIsOpen(event, true);
    }),
    trigger: "hover"
  });
  const overlayDismissProps = useOverlayLightDismiss(overlayRef, {
    hideOnEscape: true,
    hideOnLeave: true,
    hideOnOutsideClick: false,
    onHide: useEventCallback(event => {
      // Ignore events related to the trigger.
      if (!isTargetParent(event.target, triggerRef) && event.relatedTarget !== triggerRef.current) {
        updateIsOpen(event, false);
      }
    }),
    trigger: "hover"
  });
  const [trigger, tooltip] = parseTooltipTrigger(children);
  const tooltipId = useId(tooltip.props.id, "o-ui-tooltip");
  const triggerWithDescribedBy = augmentElement(trigger, {
    "aria-describedby": isOpen ? tooltipId : undefined
  }); // HACK: a disabled element doesn't fire event, therefore the element is wrapped in a div.

  const triggerElement = !triggerWithDescribedBy.props.disabled ? triggerWithDescribedBy : /*#__PURE__*/_jsx(Div, {
    className: "o-ui-tooltip-disabled-wrapper",
    children: triggerWithDescribedBy
  });
  const triggerMarkup = augmentElement(triggerElement, mergeProps(triggerProps, {
    ref: triggerRef
  }));
  const tooltipMarkup = augmentElement(tooltip, {
    id: tooltipId
  });
  return /*#__PURE__*/_jsxs(TooltipTriggerContext.Provider, {
    value: {
      isOpen
    },
    children: [triggerMarkup, /*#__PURE__*/_jsxs(Overlay, { ...mergeProps(rest, {
        as,
        borderOffset: themeAccessor.getSpace(3),
        ref: overlayRef,
        show: isOpen,
        zIndex
      }, overlayDismissProps),
      children: [tooltipMarkup, /*#__PURE__*/_jsx(OverlayArrow, {
        ref: arrowRef
      })]
    })]
  });
}
InnerTooltipTrigger.defaultElement = DefaultElement;
export const TooltipTrigger = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerTooltipTrigger, { ...props,
  forwardedRef: ref
}));